import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Input } from '@/components/ui/input';
import { SingleSelectFilter } from '@/components/ui/single-select-filter';
import { ApiError, api } from '@/lib/api';
import { variantForTag } from '@/lib/tag-color';
import { extractVariableKeys } from '@/lib/template-vars';
import type {
  ResponsePromptDetail,
  ResponsePromptVariable,
  ResponsePromptVariant,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, FlaskConical, History, RotateCcw, Save } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { HighlightedTextarea } from './HighlightedTextarea';
import { SaveVersionModal } from './SaveVersionModal';
import { TestCasesModal } from './TestCasesModal';
import { TestRunArea } from './TestRunArea';
import { VariantAddModal } from './VariantAddModal';
import { VariantRenameModal } from './VariantRenameModal';
import { VariantTabs } from './VariantTabs';
import { VersionsModal } from './VersionsModal';

interface Props {
  fileId: string;
  /** dirty 상태가 바뀔 때 부모에 알림 — 다른 파일 선택 시 확인용. */
  onDirtyChange?: (dirty: boolean) => void;
  /** 모바일에서 좌측 트리로 돌아가기 — 정의된 경우만 "< 트리로" 버튼 노출. */
  onBackToTree?: () => void;
}

function variantLabel(v: ResponsePromptVariant): string {
  if (v.label?.trim()) return v.label.trim();
  if (v.modelMatch === '') return '기본';
  return v.modelMatch;
}

export function PromptEditorPanel({ fileId, onDirtyChange, onBackToTree }: Props) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['admin', 'response-prompts', 'detail', fileId],
    queryFn: () => api.responsePromptDetail(fileId),
  });
  const detail: ResponsePromptDetail | undefined = detailQ.data;

  // 박물관의 prompt 단위 기본 endpoint 선택용. TestRunArea 와 같은 캐시 키 공유.
  const endpointsQ = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    staleTime: 60_000,
  });
  const setPreferredEndpointMut = useMutation({
    mutationFn: (endpointId: string | null) =>
      api.responsePromptSetPreferredEndpoint(fileId, endpointId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts', 'detail', fileId] });
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts', 'tree'] });
    },
    onError: (err) => {
      window.alert(err instanceof ApiError ? err.message : '기본 endpoint 설정에 실패했습니다.');
    },
  });

  // 현재 편집 중인 variant. 파일 전환 / 신규 로드 시 default 자동 선택.
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [content, setContent] = useState<string>('');
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [varMeta, setVarMeta] = useState<
    Record<string, { type?: 'string' | 'number' | 'enum'; enumOptions?: string[] }>
  >({});

  const seedFromVariant = (v: ResponsePromptVariant) => {
    setContent(v.currentContent ?? '');
    const obj: Record<string, string> = {};
    const meta: Record<string, { type?: 'string' | 'number' | 'enum'; enumOptions?: string[] }> =
      {};
    for (const vv of v.currentVariables) {
      obj[vv.key] = vv.defaultValue;
      if (vv.type || vv.enumOptions) {
        const m: { type?: 'string' | 'number' | 'enum'; enumOptions?: string[] } = {};
        if (vv.type) m.type = vv.type;
        if (vv.enumOptions) m.enumOptions = vv.enumOptions;
        meta[vv.key] = m;
      }
    }
    setDefaults(obj);
    setVarMeta(meta);
  };

  // 파일 / variant / 활성 버전이 바뀔 때 본문 재시드.
  // key 는 (selectedVariantId, variant.currentVersion) — 같은 variant 안에서 active 가 바뀌면 자동 시드.
  const lastSeedKeyRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: detail / selectedVariantId 변화에만 반응. seedFromVariant 의 setter 들은 안정적.
  useEffect(() => {
    if (!detail || detail.node.type !== 'file') return;
    // 파일이 막 로드됐는데 아직 selectedVariantId 미설정 → default 자동 선택.
    if (selectedVariantId == null) {
      const def = detail.variants.find((v) => v.modelMatch === '') ?? detail.variants[0];
      if (def) setSelectedVariantId(def.id);
      return; // 다음 effect 사이클에 seed.
    }
    const v = detail.variants.find((x) => x.id === selectedVariantId);
    if (!v) {
      // variant 가 사라짐 (삭제됨) — default 로 복귀.
      const def = detail.variants.find((x) => x.modelMatch === '') ?? detail.variants[0];
      if (def) setSelectedVariantId(def.id);
      return;
    }
    const key = `${v.id}::${v.currentVersion ?? 'null'}`;
    if (lastSeedKeyRef.current !== key) {
      seedFromVariant(v);
      lastSeedKeyRef.current = key;
    }
  }, [detail, selectedVariantId]);

  // 파일 전환은 부모가 `key={selectedFileId}` 로 unmount 시키므로 별도 reset effect 불필요 —
  // 새 mount 시점에 selectedVariantId / lastSeedKeyRef 모두 초기값으로 시작.

  const selectedVariant: ResponsePromptVariant | null = useMemo(() => {
    if (!detail || detail.node.type !== 'file' || !selectedVariantId) return null;
    return detail.variants.find((v) => v.id === selectedVariantId) ?? null;
  }, [detail, selectedVariantId]);

  const variantVersions = useMemo(() => {
    if (!detail || !selectedVariant) return [];
    return detail.versions.filter((v) => v.variantId === selectedVariant.id);
  }, [detail, selectedVariant]);

  const variableKeys = useMemo(() => extractVariableKeys(content), [content]);
  const variables: ResponsePromptVariable[] = useMemo(
    () =>
      variableKeys.map((k) => {
        const m = varMeta[k];
        const v: ResponsePromptVariable = { key: k, defaultValue: defaults[k] ?? '' };
        if (m?.type) v.type = m.type;
        if (m?.enumOptions && m.enumOptions.length > 0) v.enumOptions = m.enumOptions;
        return v;
      }),
    [variableKeys, defaults, varMeta],
  );

  const varInfo = useMemo(() => {
    const obj: Record<string, { defaultValue: string; type?: 'string' | 'number' | 'enum' }> = {};
    for (const k of variableKeys) {
      const entry: { defaultValue: string; type?: 'string' | 'number' | 'enum' } = {
        defaultValue: defaults[k] ?? '',
      };
      const t = varMeta[k]?.type;
      if (t) entry.type = t;
      obj[k] = entry;
    }
    return obj;
  }, [variableKeys, defaults, varMeta]);

  const dirty = useMemo(() => {
    if (!selectedVariant) return false;
    return (
      content !== (selectedVariant.currentContent ?? '') ||
      JSON.stringify(variables) !== JSON.stringify(selectedVariant.currentVariables)
    );
  }, [selectedVariant, content, variables]);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ─── Variant CRUD modals ─────────────────────────────────────────────────
  const [variantAddOpen, setVariantAddOpen] = useState(false);
  const [variantRenameId, setVariantRenameId] = useState<string | null>(null);

  const variantCreateMut = useMutation({
    mutationFn: (body: { modelMatch: string; label?: string; temperature?: number }) =>
      api.responsePromptVariantCreate(fileId, body),
    // detail 이 새 variant 를 포함하기 전에 setSelectedVariantId 를 호출하면
    // seed effect 가 "selectedVariantId 가 detail 에 없네 → default 로 복귀" 로 덮어쓴다.
    // invalidate 를 await 해서 refetch 완료 후 전환 + 모달 닫기.
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] });
      setSelectedVariantId(created.id);
      lastSeedKeyRef.current = null;
      setVariantAddOpen(false);
    },
  });

  const variantRenameMut = useMutation({
    mutationFn: (input: {
      variantId: string;
      body: { label?: string | null; temperature?: number | null };
    }) => api.responsePromptVariantRename(fileId, input.variantId, input.body),
    onSuccess: () => {
      setVariantRenameId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] });
    },
  });

  const variantDeleteMut = useMutation({
    mutationFn: (variantId: string) => api.responsePromptVariantDelete(fileId, variantId),
    onSuccess: (_, variantId) => {
      // 삭제된 게 현재 선택이면 default 로 전환 (effect 가 처리).
      if (selectedVariantId === variantId) setSelectedVariantId(null);
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] });
    },
  });

  // ─── Save / Activate / Tags ──────────────────────────────────────────────
  const [saveOpen, setSaveOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [testCasesOpen, setTestCasesOpen] = useState(false);

  const saveMut = useMutation({
    mutationFn: (body: { changelog: string; activate: boolean; tags: string[] }) => {
      if (!selectedVariant) throw new Error('variant 미선택 상태로 저장 시도');
      return api.responsePromptSaveVersion(fileId, {
        variantId: selectedVariant.id,
        content,
        variables,
        changelog: body.changelog || undefined,
        activate: body.activate,
        tags: body.tags,
      });
    },
    onSuccess: () => {
      setSaveOpen(false);
      qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] });
    },
  });

  const activateMut = useMutation({
    mutationFn: (versionId: string) => api.responsePromptActivateVersion(fileId, versionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] }),
  });

  const tagsMut = useMutation({
    mutationFn: (input: { versionId: string; tags: string[] }) =>
      api.responsePromptUpdateVersionTags(fileId, input.versionId, { tags: input.tags }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'response-prompts'] }),
  });

  // ─── variant 전환 (dirty 가드) ───────────────────────────────────────────
  const switchVariant = (id: string) => {
    if (id === selectedVariantId) return;
    if (dirty && selectedVariant) {
      const target = detail?.variants.find((v) => v.id === id);
      const label = target ? variantLabel(target) : '다른 variant';
      if (
        !window.confirm(
          `현재 ${variantLabel(selectedVariant)} 의 미저장 변경이 사라집니다.\n${label} 로 이동할까요?`,
        )
      ) {
        return;
      }
    }
    setSelectedVariantId(id);
    lastSeedKeyRef.current = null;
  };

  const deleteVariant = (id: string) => {
    const target = detail?.variants.find((v) => v.id === id);
    if (!target) return;
    const versionCount = detail?.versions.filter((v) => v.variantId === id).length ?? 0;
    const versionMsg = versionCount > 0 ? `${versionCount} 개 버전이 함께 삭제됩니다.\n` : '';
    if (
      !window.confirm(
        `${variantLabel(target)} variant 를 삭제합니다.\n${versionMsg}계속하시겠어요?`,
      )
    ) {
      return;
    }
    variantDeleteMut.mutate(id);
  };

  if (detailQ.isLoading) {
    return <div className="p-6 text-sm text-foreground-muted">불러오는 중…</div>;
  }
  if (!detail) {
    return <div className="p-6 text-sm text-destructive">프롬프트를 찾을 수 없습니다.</div>;
  }
  if (detail.node.type !== 'file') {
    return <div className="p-6 text-sm text-foreground-muted">선택한 노드가 폴더입니다.</div>;
  }
  if (!selectedVariant) {
    return <div className="p-6 text-sm text-foreground-muted">variant 불러오는 중…</div>;
  }

  const activeVersion = variantVersions.find((v) => v.version === selectedVariant.currentVersion);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        {onBackToTree ? (
          <button
            type="button"
            onClick={onBackToTree}
            className="lg:hidden shrink-0 inline-flex items-center text-xs text-foreground-muted hover:text-foreground"
            aria-label="트리로 돌아가기"
          >
            <ChevronLeft className="size-4" />
            트리
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {detail.node.slug ? (
              <div className="shrink-0 inline-flex items-center gap-0.5 rounded bg-surface-muted pl-1.5 pr-0.5 py-0.5">
                <code className="text-xs text-foreground-muted">{detail.node.slug}</code>
                <CopyButton text={detail.node.slug} label="slug 복사" size="xs" />
              </div>
            ) : null}
            <h2 className="truncate text-base font-semibold">{detail.node.name}</h2>
            {selectedVariant.currentVersion ? (
              <Badge variant="accent">v{selectedVariant.currentVersion}</Badge>
            ) : (
              <Badge variant="muted">아직 활성 버전 없음</Badge>
            )}
            {activeVersion?.tags.map((t) => (
              <Badge key={t} variant={variantForTag(t)}>
                {t}
              </Badge>
            ))}
          </div>
          {detail.node.description ? (
            <div className="mt-0.5 truncate text-xs text-foreground-muted">
              {detail.node.description}
            </div>
          ) : null}
        </div>
        <div className="text-xs text-foreground-muted shrink-0">
          {content.length.toLocaleString()}자 · 변수 {variableKeys.length}개
          {dirty ? <span className="ml-2 text-accent">● 미저장</span> : null}
        </div>
        {dirty ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              if (
                window.confirm(
                  `편집한 내용을 모두 버리고 현재 ${variantLabel(selectedVariant)} variant 의 활성 버전 (v${selectedVariant.currentVersion ?? '없음'}) 으로 되돌립니다.`,
                )
              ) {
                seedFromVariant(selectedVariant);
                lastSeedKeyRef.current = `${selectedVariant.id}::${selectedVariant.currentVersion ?? 'null'}`;
              }
            }}
            title="현재 활성 버전으로 되돌리기"
          >
            <RotateCcw className="size-4 mr-1" />
            되돌리기
          </Button>
        ) : null}
        <Button type="button" variant="secondary" size="sm" onClick={() => setTestCasesOpen(true)}>
          <FlaskConical className="size-4 mr-1" />
          케이스
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={() => setVersionsOpen(true)}>
          <History className="size-4 mr-1" />
          버전
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => setSaveOpen(true)}
          disabled={!content.trim() || !dirty}
        >
          <Save className="size-4 mr-1" />
          저장
        </Button>
      </div>

      {/*
        프롬프트 단위 기본 endpoint — invokeResponsePrompt / prompt.run 의 endpoint 결정 2 단계.
        미설정 시 호출자 컨텍스트 (챗 세션 endpoint) 가 fallback.
      */}
      <div className="flex items-center gap-2 border-b border-border bg-surface-muted/30 px-4 py-1.5 text-xs">
        <span className="text-foreground-muted shrink-0">기본 endpoint</span>
        <div className="w-64">
          <SingleSelectFilter
            options={(endpointsQ.data?.endpoints ?? []).map((ep) => ({
              id: ep.id,
              label: ep.name,
              hint: ep.model,
            }))}
            value={detail.node.preferredEndpointId}
            onChange={(id) => setPreferredEndpointMut.mutate(id)}
            triggerPlaceholder="(설정 안 함 — 호출자 컨텍스트)"
            searchPlaceholder="엔드포인트 검색…"
          />
        </div>
        <span className="text-foreground-muted">
          미설정이면 호출 측의 endpoint (챗 세션 endpoint 또는 default) 사용.
        </span>
      </div>

      <VariantTabs
        variants={detail.variants}
        activeVariantId={selectedVariant.id}
        onSelect={switchVariant}
        onAdd={() => setVariantAddOpen(true)}
        onRename={(id) => setVariantRenameId(id)}
        onDelete={deleteVariant}
      />

      {/* Body — 좌(에디터) + 우(변수/테스트) */}
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_minmax(0,420px)]">
        <div className="flex min-h-0 flex-col border-r border-border">
          <div className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs text-foreground-muted">
            <span>본문 — `{'{{변수}}'}` 구문은 자동 추출됩니다.</span>
            <span className="ml-auto inline-flex items-center gap-1.5">
              <span>현재 편집 중:</span>
              <strong className="text-foreground">{variantLabel(selectedVariant)}</strong>
              {selectedVariant.modelMatch !== '' ? (
                <span>(model = {selectedVariant.modelMatch})</span>
              ) : null}
              {selectedVariant.temperature != null ? (
                <span>· T = {selectedVariant.temperature}</span>
              ) : null}
            </span>
          </div>
          <HighlightedTextarea
            value={content}
            onChange={setContent}
            varInfo={varInfo}
            placeholder="시스템 프롬프트 본문을 입력하세요. 예: 다음 질문의 언어를 한 단어로 분류하라: {{question}}"
            className="flex-1 min-h-0"
          />
        </div>
        <div className="flex min-h-0 flex-col">
          <div className="border-b border-border px-4 py-2 text-xs text-foreground-muted">
            변수 기본값
          </div>
          {variableKeys.length === 0 ? (
            <div className="px-4 py-3 text-xs text-foreground-muted">
              본문에 `{'{{이름}}'}` 형식의 변수를 적으면 여기에 자동으로 나타납니다.
            </div>
          ) : (
            <div className="space-y-3 px-4 py-3">
              {variableKeys.map((k) => {
                const hasValue = (defaults[k] ?? '').length > 0;
                const meta = varMeta[k] ?? {};
                const type = meta.type ?? 'string';
                const enumOptions = meta.enumOptions ?? [];
                const setMetaPatch = (
                  patch: Partial<{ type: 'string' | 'number' | 'enum'; enumOptions: string[] }>,
                ) => setVarMeta({ ...varMeta, [k]: { ...meta, ...patch } });
                return (
                  <div key={k} className="space-y-1">
                    <label
                      className="flex items-center gap-1.5 text-xs text-foreground-muted"
                      htmlFor={`rp-default-${k}`}
                    >
                      <span>{`{{${k}}}`}</span>
                      {!hasValue ? (
                        <span className="text-[10px] text-accent">기본값 미설정</span>
                      ) : null}
                      <select
                        value={type}
                        onChange={(e) => {
                          const next = e.target.value as 'string' | 'number' | 'enum';
                          setMetaPatch({ type: next });
                        }}
                        className="ml-auto h-5 rounded border border-border bg-surface px-1 text-[10px]"
                      >
                        <option value="string">문자열</option>
                        <option value="number">숫자</option>
                        <option value="enum">선택지</option>
                      </select>
                    </label>
                    {type === 'enum' ? (
                      <>
                        <Input
                          value={enumOptions.join(', ')}
                          onChange={(e) => {
                            const opts = e.target.value
                              .split(',')
                              .map((s) => s.trim())
                              .filter((s) => s.length > 0);
                            setMetaPatch({ enumOptions: opts });
                          }}
                          placeholder="옵션을 쉼표로 구분 (예: 한국어, 영어, 일본어)"
                          className="h-7 text-xs"
                        />
                        {enumOptions.length > 0 ? (
                          <select
                            id={`rp-default-${k}`}
                            value={defaults[k] ?? ''}
                            onChange={(e) => setDefaults({ ...defaults, [k]: e.target.value })}
                            className="h-8 w-full rounded-md border border-border bg-surface px-2 text-sm"
                          >
                            <option value="">— 기본값 선택 —</option>
                            {enumOptions.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="text-[10px] text-foreground-muted">
                            옵션을 1개 이상 추가하세요.
                          </div>
                        )}
                      </>
                    ) : (
                      <Input
                        id={`rp-default-${k}`}
                        type={type === 'number' ? 'number' : 'text'}
                        value={defaults[k] ?? ''}
                        onChange={(e) => setDefaults({ ...defaults, [k]: e.target.value })}
                        placeholder={type === 'number' ? '예: 5' : '테스트 시 적용될 기본값'}
                        className="h-8 text-sm"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-auto border-t border-border">
            <TestRunArea
              content={content}
              variableValues={defaults}
              variants={detail.variants}
              editingVariantId={selectedVariant.id}
            />
          </div>
        </div>
      </div>

      <SaveVersionModal
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        initialTags={activeVersion?.tags ?? []}
        isPending={saveMut.isPending}
        onSubmit={(body) => saveMut.mutate(body)}
      />
      <VersionsModal
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        versions={variantVersions}
        currentVersion={selectedVariant.currentVersion}
        activatePending={activateMut.isPending}
        tagsPending={tagsMut.isPending}
        onActivate={(versionId) => activateMut.mutate(versionId)}
        onSetTags={(versionId, tags) => tagsMut.mutate({ versionId, tags })}
      />
      <TestCasesModal
        open={testCasesOpen}
        onClose={() => setTestCasesOpen(false)}
        fileId={fileId}
        currentContent={content}
        currentVariables={variables}
        variants={detail.variants}
      />

      <VariantAddModal
        open={variantAddOpen}
        onClose={() => setVariantAddOpen(false)}
        existingModelMatches={detail.variants.map((v) => v.modelMatch)}
        isPending={variantCreateMut.isPending}
        onSubmit={(body) => variantCreateMut.mutate(body)}
      />
      <VariantRenameModal
        open={variantRenameId !== null}
        onClose={() => setVariantRenameId(null)}
        variant={detail.variants.find((v) => v.id === variantRenameId) ?? null}
        isPending={variantRenameMut.isPending}
        onSubmit={(body) => {
          if (variantRenameId) variantRenameMut.mutate({ variantId: variantRenameId, body });
        }}
      />

      {(() => {
        const err =
          saveMut.error ??
          activateMut.error ??
          tagsMut.error ??
          variantCreateMut.error ??
          variantRenameMut.error ??
          variantDeleteMut.error;
        if (!err) return null;
        const label = saveMut.error
          ? '저장 실패'
          : activateMut.error
            ? '버전 활성화 실패'
            : tagsMut.error
              ? '태그 저장 실패'
              : variantCreateMut.error
                ? 'variant 추가 실패'
                : variantRenameMut.error
                  ? 'variant 수정 실패'
                  : 'variant 삭제 실패';
        const msg = err instanceof ApiError ? err.message : label;
        return (
          <div className="border-t border-border bg-destructive/5 px-4 py-2 text-sm text-destructive">
            {label}: {msg}
          </div>
        );
      })()}
    </div>
  );
}
